import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { getIngredientFamily } from '../constants/IngredientFamilies';

// Icons mapping for categories if images are missing
const CATEGORY_ICONS = {
    vegetables: 'eco',
    fruits: 'nutrition',
    meat: 'set_meal',
    dairy: 'egg',
    pantry: 'kitchen',
    spices: 'local_dining'
};

const isDairyProduct = (itemName) => {
    if (!itemName) return false;
    const lowerName = itemName.toLowerCase();
    return (lowerName === 'süt' || lowerName.includes('çiğ süt') || lowerName.includes('köy sütü') || (lowerName.includes('süt') && !lowerName.includes('yoğurt') && !lowerName.includes('peynir') && !lowerName.includes('tatlı')));
};

const WIZARD_STEPS = [
    {
        id: 1,
        title: 'Sebze & Yeşillik',
        icon: 'eco',
        items: [
            { id: 'tomato', name: 'Domates', dictionaryId: 'cmltr5d4x0000lvscj6np0el6', image: '/assets/wizard/tomatoes.webp', icon: 'nutrition' },
            { id: 'cucumber', name: 'Salatalık', dictionaryId: 'cmltr5dip001slvsc2hsz0mlz', image: '/assets/wizard/cucumber.webp', icon: 'nutrition' },
            { id: 'onion', name: 'Soğan', dictionaryId: 'cmltr5d91000hlvscu6qgt0v4', image: '/assets/wizard/onion.webp', icon: 'spa' },
            { id: 'garlic', name: 'Sarımsak', dictionaryId: 'cmltr5dad000nlvsczqgn2yh5', image: '/assets/wizard/garlic.webp', icon: 'spa' },
            { id: 'potato', name: 'Patates', dictionaryId: 'cmltr5db8000rlvscc1733b6e', image: '/assets/wizard/potatoes.webp', icon: 'egg' },
            { id: 'pepper', name: 'Biber', dictionaryId: 'cmltr5d780008lvscn3w08qyf', image: '/assets/wizard/pepper.webp', icon: 'local_florist' },
            { id: 'carrot', name: 'Havuç', dictionaryId: 'cmltr5dca000wlvsc4d06lw09', image: '/assets/wizard/carrot.webp', icon: 'grass' },
            { id: 'lettuce', name: 'Marul', dictionaryId: 'cmltr5dfm001dlvsce8geuw43', image: '/assets/wizard/lettuce.webp', icon: 'filter_vintage' },
        ]
    },
    {
        id: 2,
        title: 'Meyve',
        icon: 'nutrition',
        items: [
            { id: 'apple', name: 'Elma', dictionaryId: 'cmltr5ea6005ulvscel0ag68j', image: '/assets/wizard/apple.webp', icon: 'nutrition' },
            { id: 'banana', name: 'Muz', dictionaryId: 'cmltr5ebe0061lvscdpms5mb1', image: '/assets/wizard/banana.webp', icon: 'nutrition' },
            { id: 'orange', name: 'Portakal', dictionaryId: 'cmltr5ec40065lvscm983aqxr', image: '/assets/wizard/orange.webp', icon: 'nutrition' },
            { id: 'lemon', name: 'Limon', dictionaryId: 'cmltr5eco0067lvscwymr4iyt', image: '/assets/wizard/lemon.webp', icon: 'nutrition' }
        ]
    },
    {
        id: 3,
        title: 'Et & Balık',
        icon: 'set_meal',
        items: [
            { id: 'chicken', name: 'Tavuk', dictionaryId: 'cmltr5dji001wlvsca3h9w340', image: '/assets/wizard/chicken.webp', icon: 'set_meal' },
            { id: 'mince', name: 'Kıyma', dictionaryId: 'cmltr5dlo0028lvsc5zojxdyd', image: '/assets/wizard/mince.webp', icon: 'lunch_dining' }, // Anchor: Dana Kıyma
            { id: 'egg', name: 'Yumurta', dictionaryId: 'cmltr5dq3002wlvscryn0ikza', image: '/assets/wizard/egg.webp', icon: 'egg' },
            { id: 'fish', name: 'Balık', dictionaryId: 'cmltr5dng002ilvsccb99f8jf', image: '/assets/wizard/fish.webp', icon: 'set_meal' }
        ]
    },
    {
        id: 4,
        title: 'Süt Ürünleri',
        icon: 'egg',
        items: [
            { id: 'milk', name: 'Süt', dictionaryId: 'cmltr5drj0034lvscef6nz704', image: '/assets/wizard/milk.webp', icon: 'local_drink' },
            { id: 'yogurt', name: 'Yoğurt', dictionaryId: 'cmltr5dsg0039lvsce7lgydx7', image: '/assets/wizard/yogurt.webp', icon: 'icecream' },
            { id: 'cheese', name: 'Peynir', dictionaryId: 'cmltr5dtd003elvscb6f26phz', image: '/assets/wizard/cheese.webp', icon: 'favorite' },
            { id: 'butter', name: 'Tereyağı', dictionaryId: 'cmltr5e22004rlvsclwiet50k', image: '/assets/wizard/butter.webp', icon: 'blur_on' }
        ]
    },
    {
        id: 5,
        title: 'Kuru Gıdalar',
        icon: 'kitchen',
        items: [
            { id: 'rice', name: 'Pirinç', dictionaryId: 'cmltr5dwk003vlvscnym1yqsn', image: '/assets/wizard/rice.webp', icon: 'grain' },
            { id: 'pasta', name: 'Makarna', dictionaryId: 'cmltr5dya0044lvsc9wtqvo9e', image: '/assets/wizard/pasta.webp', icon: 'ramen_dining' },
            { id: 'bulgur', name: 'Bulgur', dictionaryId: 'cmltr5dxm0040lvsch1waxz50', image: '/assets/wizard/bulgur-wheat.webp', icon: 'grain' },
            { id: 'flour', name: 'Un', dictionaryId: 'cmltr5dzj004blvsc4c1qx0sw', image: '/assets/wizard/flour.webp', icon: 'waves' }
        ]
    },
    {
        id: 6,
        title: 'Baharat',
        icon: 'local_dining',
        items: [
            { id: 'cumin', name: 'Kimyon', dictionaryId: 'cmltr5e8k005mlvscicuujkrj', image: '/assets/wizard/cumin.webp', icon: 'spa' },
            { id: 'thyme', name: 'Kekik', dictionaryId: 'cmltr5e8s005nlvsceb7xuea0', image: '/assets/wizard/oregano.webp', icon: 'spa' }
        ]
    }
];

export default function QuickWizard({ onComplete, onClose }) {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [selections, setSelections] = useState({}); // { itemId: { quantity: 'az'|'orta'|'cok' } }
    const [modalItem, setModalItem] = useState(null); // Item being edited
    const [selectedQty, setSelectedQty] = useState('orta'); // New: track quantity in modal
    const [estimatedPrice, setEstimatedPrice] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (modalItem) {
            const fetchPrice = async () => {
                try {
                    const token = localStorage.getItem('authToken');
                    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
                    const servingSize = profile.servingSize || 4;

                    const res = await fetch(`${API_BASE_URL}/api/inventory/classify?name=${encodeURIComponent(modalItem.name)}&quantity=${selectedQty}&servingSize=${servingSize}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if (data && data.estimated_price) {
                        setEstimatedPrice(data.estimated_price);
                    } else {
                        setEstimatedPrice(null);
                    }
                } catch (err) {
                    console.error('Price fetch error:', err);
                    setEstimatedPrice(null);
                }
            };
            fetchPrice();
        } else {
            setEstimatedPrice(null);
        }
    }, [modalItem, selectedQty]); // Refetch when quantity changes too


    const currentStep = WIZARD_STEPS[currentStepIndex];
    const stepCount = WIZARD_STEPS.length;
    const progress = ((currentStepIndex + 1) / stepCount) * 100;

    // Helper to get image or icon
    const getItemVisual = (item) => item.image || null;

    const handleItemClick = (item) => {
        // Open modal to select quantity
        const existing = selections[item.id];
        setSelectedQty(existing ? existing.quantity : 'orta');
        setModalItem(item);
    };

    const handleConfirmSelection = () => {
        if (!modalItem) return;
        setSelections(prev => ({
            ...prev,
            [modalItem.id]: {
                quantity: selectedQty,
                item: modalItem,
                isPasteurized: isDairyProduct(modalItem.name) ? true : undefined
            }
        }));
        setModalItem(null);
    };


    const handleRemove = (itemId) => {
        setSelections(prev => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
    };

    const handleNext = () => {
        if (currentStepIndex < stepCount - 1) {
            setCurrentStepIndex(prev => prev + 1);
        } else {
            // Finished
            handleSubmit();
        }
    };

    const handleSkip = () => {
        if (currentStepIndex < stepCount - 1) {
            setCurrentStepIndex(prev => prev + 1);
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        const items = Object.values(selections).map(s => ({
            name: s.item.name,
            quantity: s.quantity,
            dictionaryId: s.item.dictionaryId, // 🔗 [V23] Frictionless Entry
            is_pasteurized: s.isPasteurized ?? true // Default to true
        }));

        if (items.length === 0) {
            if (onComplete) onComplete([]);
            return;
        }

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/inventory/bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ items })
            });

            if (!res.ok) throw new Error('Failed to save items');

            const data = await res.json();
            console.log('Saved items:', data);

            if (onComplete) onComplete(items);

        } catch (err) {
            console.error('Wizard submit error:', err);
            alert('Kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentStepSelections = Object.values(selections).filter(s =>
        currentStep.items.find(i => i.id === s.item.id)
    );

    const activeStepSelectionCount = currentStepSelections.length;

    return (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in">
            {/* Main Container */}
            <div className="bg-white dark:bg-slate-800 w-full max-w-[1000px] h-[85vh] max-h-[700px] rounded-3xl shadow-2xl flex flex-col md:flex-row overflow-hidden relative ring-1 ring-white/10">

                {/* LEFT/TOP: Navigation & Progress */}
                <aside className="md:w-[80px] md:border-r border-b md:border-b-0 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-row md:flex-col items-center justify-between md:justify-start p-4 md:py-6 z-20 shrink-0">
                    <div className="hidden md:flex flex-col gap-4 w-full">
                        {WIZARD_STEPS.map((step, idx) => {
                            const isActive = idx === currentStepIndex;
                            const isCompleted = idx < currentStepIndex;
                            return (
                                <button
                                    key={step.id}
                                    onClick={() => !isSubmitting && setCurrentStepIndex(idx)}
                                    disabled={isSubmitting}
                                    className={`group w-full flex flex-col items-center gap-1 relative ${isActive ? 'text-primary' : isCompleted ? 'text-green-500' : 'text-slate-400'}`}
                                >
                                    <div className={`p-2.5 rounded-xl transition-all duration-300 ${isActive ? 'bg-red-50 dark:bg-red-900/20 ring-1 ring-red-100 dark:ring-red-900/40 shadow-sm' : ''}`}>
                                        <span className="material-symbols-outlined text-[24px]">{isCompleted ? 'check_circle' : step.icon}</span>
                                    </div>
                                    <span className="text-[9px] font-bold tracking-wide text-center hidden md:block">{step.title.split(' ')[0]}</span>
                                    {isActive && <div className="hidden md:block absolute -right-[17px] top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-l-full"></div>}
                                </button>
                            );
                        })}
                    </div>

                    {/* Mobile Header Steps Indicator */}
                    <div className="md:hidden flex items-center gap-2 w-full">
                        <span className="material-symbols-outlined text-primary text-2xl">{currentStep.icon}</span>
                        <div className="flex-1">
                            <h2 className="text-sm font-bold text-slate-800 dark:text-white leading-tight">{currentStep.title}</h2>
                            <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full mt-1 overflow-hidden">
                                <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }}></div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-bold text-slate-400">{currentStepIndex + 1}/{stepCount}</span>
                            <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                                <span className="material-symbols-outlined text-[20px] text-slate-500">close</span>
                            </button>
                        </div>
                    </div>
                </aside>

                {/* MIDDLE: Main Content */}
                <main className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-[#0f172a] relative">

                    <header className="hidden md:flex bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 px-8 py-6 items-center justify-between shrink-0">
                        <div>
                            <h1 className="text-xl font-extrabold text-slate-800 dark:text-white tracking-tight">Hızlı Mutfak Kurulumu</h1>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Sık kullandığın ürünleri seçerek envanterini oluştur.</p>
                        </div>
                        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700/50 px-3 py-1.5 rounded-full">
                            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Adım {currentStepIndex + 1}: {currentStep.title}</span>
                        </div>
                    </header>

                    <div className="flex-1 overflow-y-auto p-4 md:p-8">
                        <div className="flex items-center justify-between mb-4 px-1">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Seçenekler</h3>
                            <span className="text-[10px] bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 px-2 py-0.5 rounded text-slate-500 dark:text-slate-300">
                                {currentStep.items.length} çeşit
                            </span>
                        </div>

                        <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 pb-[180px] md:pb-0">
                            {currentStep.items.map(item => {
                                const isSelected = !!selections[item.id];
                                const itemImg = getItemVisual(item);

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleItemClick(item)}
                                        disabled={isSubmitting}
                                        className={`group relative flex flex-col items-center p-2 rounded-2xl border transition-all duration-200 h-[130px] md:h-36
                                            ${isSelected
                                                ? 'bg-red-50 dark:bg-red-900/10 border-primary ring-1 ring-primary shadow-sm'
                                                : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-red-200 dark:hover:border-red-800 shadow-sm hover:shadow-md'
                                            }
                                        `}
                                    >
                                        {/* Selection Checkmark */}
                                        {isSelected && (
                                            <div className="absolute top-2 right-2 size-4 md:size-5 bg-primary text-white rounded-full flex items-center justify-center shadow-sm z-10 animate-scale-in">
                                                <span className="material-symbols-outlined text-[10px] md:text-[14px]">check</span>
                                            </div>
                                        )}

                                        {/* Image Container */}
                                        <div className={`w-full flex-1 flex items-center justify-center p-1 md:p-2 mb-1 md:mb-2 bg-white dark:bg-slate-700/30 rounded-xl ${!itemImg && 'border border-dashed border-slate-200 dark:border-slate-600'}`}>
                                            {itemImg ? (
                                                <img src={item.image} alt={item.name} className="w-full h-full object-contain p-2 md:p-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-sm" />
                                            ) : (
                                                <span className={`material-symbols-outlined text-[24px] md:text-[32px] transition-colors ${isSelected ? 'text-primary' : 'text-slate-300 dark:text-slate-500 group-hover:text-primary/70'}`}>
                                                    {item.icon}
                                                </span>
                                            )}
                                        </div>

                                        <span className={`text-[10px] md:text-xs font-bold transition-colors truncate w-full px-1 ${isSelected ? 'text-primary' : 'text-slate-700 dark:text-slate-200'}`}>
                                            {item.name}
                                        </span>

                                        {isSelected && (
                                            <span className="text-[8px] md:text-[9px] font-bold text-white bg-primary px-1.5 md:px-2 py-0.5 rounded-full mt-1 shadow-sm">
                                                {selections[item.id].quantity.toUpperCase()}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </main>

                {/* RIGHT/BOTTOM Summary */}
                {/* RIGHT/BOTTOM Summary - Changed from fixed to flex item within card */}
                {/* RIGHT/BOTTOM Summary - Absolute sticky for mobile */}
                <aside className="absolute bottom-0 left-0 right-0 w-full md:static md:w-[320px] bg-white dark:bg-slate-800 md:border-l border-t md:border-t-0 border-slate-100 dark:border-slate-700 z-30 shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] md:shadow-none flex flex-col">

                    <div className="hidden md:flex p-5 border-b border-slate-100 dark:border-slate-700 items-center justify-between shrink-0">
                        <h3 className="font-bold text-slate-800 dark:text-white">Seçilenler</h3>
                        {currentStepSelections.length > 0 && <button className="text-xs text-primary font-medium hover:underline">Temizle</button>}
                    </div>

                    <div className="hidden md:block flex-1 overflow-y-auto p-4 space-y-2">
                        {currentStepSelections.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-300 dark:text-slate-600 opacity-50">
                                <span className="material-symbols-outlined text-[48px] mb-2">shopping_basket</span>
                                <p className="text-sm font-medium">Henüz ürün seçmediniz</p>
                            </div>
                        ) : (
                            currentStepSelections.map((sel, idx) => (
                                <div key={sel.item.id} className="group flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-100 dark:border-slate-700 animate-fade-in-up">
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] font-bold text-slate-300 w-3">{idx + 1}</span>
                                        <div className="size-8 rounded-lg bg-white dark:bg-slate-600 flex items-center justify-center text-primary shadow-sm">
                                            {sel.item.image ? <img src={sel.item.image} className="w-5 h-5 object-contain" /> : <span className="material-symbols-outlined text-[18px]">{sel.item.icon}</span>}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{sel.item.name}</span>
                                            <span className="text-[10px] text-primary font-bold bg-red-50 dark:bg-red-900/30 w-max px-1.5 rounded">{sel.quantity.toUpperCase()}</span>
                                        </div>
                                    </div>
                                    <button onClick={() => handleRemove(sel.item.id)} className="text-slate-400 hover:text-red-500 p-1 rounded-lg transition-colors">
                                        <span className="material-symbols-outlined text-[18px]">close</span>
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="md:hidden w-full flex justify-center pt-3 pb-1">
                        <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-full"></div>
                    </div>

                    {/* Mobile Floating Bottom Bar */}
                    <div className="md:hidden absolute bottom-6 left-0 right-0 flex justify-center z-50 px-4">
                        <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md shadow-2xl shadow-black/20 rounded-full p-2 flex items-center gap-3 border border-slate-100 dark:border-slate-700 w-full max-w-[400px] animate-in slide-in-from-bottom-4">
                            <div className="flex-1 pl-3">
                                {activeStepSelectionCount === 0 ? (
                                    <p className="text-[11px] font-bold text-slate-500 animate-pulse">
                                        Seçmek için ürüne tıkla ↗
                                    </p>
                                ) : (
                                    <p className="text-[11px] font-bold text-green-600">
                                        {currentStep.title.split(' ')[0]} Seçildi {activeStepSelectionCount}
                                    </p>
                                )}
                            </div>

                            <button
                                onClick={handleNext}
                                disabled={isSubmitting}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm transition-all active:scale-95 shadow-md ${isSubmitting
                                    ? 'bg-slate-300 cursor-not-allowed'
                                    : activeStepSelectionCount > 0
                                        ? 'bg-primary hover:bg-red-600 text-white shadow-primary/20'
                                        : 'bg-slate-200 text-slate-400'
                                    }`}
                            >
                                <span>{isSubmitting ? '...' : (currentStepIndex === stepCount - 1 ? 'Tamamla' : 'Devam Et')}</span>
                                {!isSubmitting && <span className="material-symbols-outlined text-[18px]">arrow_forward</span>}
                            </button>
                        </div>
                    </div>

                    <div className="hidden md:flex p-6 bg-slate-50 dark:bg-slate-800 md:dark:bg-slate-900/30 border-t border-slate-100 dark:border-slate-700">
                        <div className="flex flex-col gap-5 mt-2 w-full">
                            <button
                                onClick={handleNext}
                                disabled={isSubmitting}
                                className="w-full bg-primary hover:bg-red-600 text-white py-4 rounded-xl font-bold text-base shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span>{isSubmitting ? '...' : (currentStepIndex === stepCount - 1 ? 'Tamamla' : 'Devam Et')}</span>
                                {!isSubmitting && <span className="material-symbols-outlined text-[20px]">arrow_forward</span>}
                            </button>
                            <div className="flex justify-between px-3 pb-2">
                                <button onClick={onClose} disabled={isSubmitting} className="text-xs font-extra-bold text-slate-900 dark:text-white hover:text-red-500 transition-colors">Çıkış</button>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Quantity Modal */}
                {modalItem && (
                    <div className="absolute inset-0 z-[60] bg-slate-900/20 backdrop-blur-[2px] flex items-center justify-center animate-fade-in p-4">
                        <div className="bg-white dark:bg-slate-800 w-full max-w-[340px] rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-600 p-6 flex flex-col items-center gap-5 relative animate-scale-in">
                            <button onClick={() => setModalItem(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>

                            <div className="size-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center text-primary mb-1 ring-4 ring-red-50 dark:ring-red-900/10">
                                {modalItem.image ? (
                                    <img src={modalItem.image} className="w-10 h-10 object-contain" />
                                ) : (
                                    <span className="material-symbols-outlined text-[32px]">{modalItem.icon}</span>
                                )}
                            </div>

                            <div className="text-center w-full">
                                <h3 className="font-extrabold text-slate-800 dark:text-white text-xl">{modalItem.name}</h3>

                                <div className="flex flex-col items-center gap-1 mt-2">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Bu üründen elinizde ne kadar var?</p>
                                    {estimatedPrice && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-800">
                                            Tahmini: {estimatedPrice} ₺
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 w-full">
                                {[
                                    { val: 'az', label: 'Az', icon: 'water_drop' },
                                    { val: 'orta', label: 'Orta', icon: 'opacity' },
                                    { val: 'cok', label: 'Çok', icon: 'waves' }
                                ].map(opt => (
                                    <label key={opt.val} className="cursor-pointer group">
                                        <input
                                            type="radio"
                                            name="quantity"
                                            value={opt.val}
                                            checked={selectedQty === opt.val}
                                            className="peer sr-only"
                                            onChange={() => setSelectedQty(opt.val)}
                                        />
                                        <div className="h-[90px] rounded-xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col items-center justify-center gap-1 transition-all
                        hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-700
                        peer-checked:border-primary peer-checked:bg-red-50 dark:peer-checked:bg-red-900/20 peer-checked:text-primary
                      ">
                                            <span className="material-symbols-outlined text-[28px] text-slate-400 dark:text-slate-500 peer-checked:text-primary group-hover:text-primary/70">{opt.icon}</span>
                                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 peer-checked:text-primary">{opt.label}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>

                            <button
                                onClick={handleConfirmSelection}
                                className="w-full bg-primary hover:bg-red-600 text-white py-3.5 rounded-xl font-bold text-base shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2 active:scale-95"
                            >
                                <span>{selections[modalItem.id] ? 'Güncelle' : 'Listeye Ekle'}</span>
                            </button>


                            {/* Pasteurization Check (For Dairy) */}
                            {isDairyProduct(modalItem.name) && (
                                <div className="w-full bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800 animate-fade-in-up">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={selections[modalItem.id]?.isPasteurized ?? true}
                                                onChange={(e) => {
                                                    setSelections(prev => ({
                                                        ...prev,
                                                        [modalItem.id]: {
                                                            ...(prev[modalItem.id] || { quantity: 'orta', item: modalItem }),
                                                            isPasteurized: e.target.checked
                                                        }
                                                    }));
                                                }}
                                                className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 checked:bg-blue-600 checked:border-blue-600 transition-all"
                                            />
                                            <span className="material-symbols-outlined absolute opacity-0 peer-checked:opacity-100 text-white pointer-events-none text-lg left-0 top-0 h-5 w-5 flex items-center justify-center">
                                                check
                                            </span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-blue-900 dark:text-blue-300">Pastörize Ürün</span>
                                            <span className="text-[10px] text-blue-700 dark:text-blue-400">Market ürünleri pastörizedir.</span>
                                        </div>
                                    </label>
                                </div>
                            )}


                        </div>
                    </div>
                )}

            </div>
        </div >
    );
}
